import { DateTime } from 'luxon';
import { AxisInterval } from 'recharts/types/util/types';

import { Period, UnixTime } from 'dashboard/lib/unixTime';
import { Iso8601DateString } from 'lib/types';

export interface DatumWithTimestamp {
  created_timestamp: number;
}

// This function is designed to run the domain calculations for the granularities specified per
// figure in Analytics like Verification, Reports, etc. This will offload the logic to the client +
// Allow us to support multiple domains
export const generateDomain = (
  dateRange: [UnixTime | undefined, UnixTime | undefined],
  period: Period,
): [UnixTime, UnixTime] => {
  if (dateRange[0] == null || dateRange[1] == null) {
    throw new Error(`Undefined date range`);
  }

  // Default variables
  const dateStart = DateTime.fromSeconds(dateRange[0]);
  const dateEnd = DateTime.fromSeconds(dateRange[1]);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  let domainStart: DateTime = undefined;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  let domainEnd: DateTime = undefined;

  switch (period) {
    case Period.Day:
      domainStart = dateStart;
      break;
    case Period.Week:
      // https://github.com/moment/luxon/blob/master/docs/intl.md#locale-based-weeks-with-math
      domainStart = dateStart.startOf('week', { useLocaleWeeks: true });
      break;
    case Period.Hour:
      domainStart = dateStart;
      break;
    case Period.Month:
      domainStart = dateStart.startOf('month');
      break;
    default:
      throw new Error(`Unhandled date period`);
  }

  switch (period) {
    case Period.Day:
      domainEnd = dateEnd;
      break;
    case Period.Week:
      domainEnd = dateEnd.startOf('week', { useLocaleWeeks: true }).plus({ weeks: 1 });
      break;
    case Period.Hour:
      // In the event that the end date is in the future, we want to use the current hour as the end of the domain
      domainEnd =
        DateTime.local().diff(dateEnd, 'hours').hours < 0
          ? DateTime.local().startOf('hour')
          : dateEnd;
      break;
    case Period.Month:
      domainEnd = dateEnd.startOf('month').plus({ months: 1 });
      break;
    default:
      throw new Error(`Unhandled date period`);
  }

  return [domainStart.toUnixInteger(), domainEnd.toUnixInteger()];
};

// This is used by many of the charts in the dashboard. It takes a start and end date and returns the appropriate
// period to use for the chart. The period is used to determine how to group the data for the chart.
export function getPeriod(start: Iso8601DateString, end: Iso8601DateString): Period {
  // > ~ 6 months -> Months, >4 Weeks -> Weeks, >=1 Day - Days, Else Days
  const daysDiff = DateTime.fromISO(end).diff(DateTime.fromISO(start), 'days').days;

  if (daysDiff > 182) {
    return Period.Month;
  } else if (daysDiff > 28) {
    return Period.Week;
  } else if (daysDiff > 1) {
    return Period.Day;
  } else {
    return Period.Hour;
  }
}

export const generateTicks = (
  period: Period,
  domain: [UnixTime, UnixTime],
  useExclusiveDomainEnd = false,
) => {
  const results = [];
  // In front-end/dashboard/redux/thunk-actions/dashboard.ts we set the user's timezone as the default for luxon.
  // We use luxon to generate the ticks because we are stepping through day by day (or week by week), and the durations
  // of those differ around DST transitions.
  let current = DateTime.fromSeconds(domain[0]);
  const end = DateTime.fromSeconds(domain[1]);

  const withinTimeSeries = (end: DateTime) =>
    useExclusiveDomainEnd ? current < end : current <= end;

  while (withinTimeSeries(end)) {
    // Add in the unix time
    results.push(current.toUnixInteger());
    if (period === Period.Month) {
      current = current.plus({ months: 1 });
    } else if (period === Period.Day) {
      current = current.plus({ days: 1 });
    } else if (period === Period.Hour) {
      current = current.plus({ hours: 1 });
    } else {
      current = current.plus({ weeks: 1 });
    }
  }

  return results;
};

/**
 * Fills domain with placeholder entries spaced by period and merged with data.
 * This is needed to ensure there are no gaps in the categorical chart.
 * Recharts breaks when data are missing.
 *
 * https://github.com/recharts/recharts/issues/702#issuecomment-483767918
 *
 * TODO: add tests for fillInMissingFields = true
 */
export const fillChartData = <T extends DatumWithTimestamp>(
  data: T[],
  defaultInstance: Omit<T, 'created_timestamp'>,
  domain: [UnixTime, UnixTime],
  period: Period,
  fillInMissingFields: boolean,
  useExclusiveDomainEnd = false,
): (Omit<T, 'created_timestamp'> & DatumWithTimestamp)[] => {
  const ticks = generateTicks(period, domain, useExclusiveDomainEnd);
  const filledData: (Omit<T, 'created_timestamp'> & DatumWithTimestamp)[] = [];

  let dataIdx = 0;
  ticks.forEach((t) => {
    if (t === data[dataIdx]?.['created_timestamp']) {
      filledData.push(
        fillInMissingFields
          ? {
              ...defaultInstance,
              ...data[dataIdx],
            }
          : data[dataIdx],
      );
      dataIdx++;
    } else {
      filledData.push({
        created_timestamp: t,
        ...defaultInstance,
      });
    }
  });
  return filledData;
};

const DAILY_SPACED_TICK_INTERVAL = 23;

export const getEffectiveInterval = (
  recommendedPeriod: Period,
  chartPeriod: Period,
): AxisInterval => {
  // In the event that we're on target with our recommended period, we'll show all intervals

  if (recommendedPeriod === chartPeriod) {
    return 0;
  }

  // For hourly periods, we have some problems with the intervals. We'll need to do some special handling
  // We want to make it such that we return every days period, that means skipping 23 periods after each printed
  if (chartPeriod === Period.Hour) {
    return DAILY_SPACED_TICK_INTERVAL;
  }
  // Otherwise, we'll want to simply preserve the beginning and the ending. This behaves well enough for the time being
  // We may want to revisit this to improve our logic and handling here
  return 'preserveStartEnd' as AxisInterval;
};
